import Image from 'next/image'
import Layout, { LargeLayout } from 'components/layout'
import { descriptionMultiCols } from '../sharedTypes'
import { CFImage } from '@/data/portfolio/type/client-index'
import classNames from 'classnames'
import { useMemo } from 'react'

export type ImagesGridProps = {
  images: CFImage[]
  ratio?: number
  description?: descriptionMultiCols
  title?: string
  alignement?: string
  subtitle?: string
  variant?: number
  fullHeight?: boolean
  spaceBetween?: boolean
  alignTop?: boolean
  rightPadding?: boolean
  cols?: number
  mobileCols?: number
}

export default function ImagesGrid({
  images,
  ratio = 1.5,
  description = { cols: 0, content: [] },
  title = '',
  alignement = '',
  subtitle,
  variant = 0,
  fullHeight = false,
  spaceBetween = true,
  alignTop = false,
  rightPadding = false,
  cols = 2,
  mobileCols = 2
}: ImagesGridProps) {
  const imageSizes = useMemo(() => {
    let sizes = {
      minHeight: '',
      height: '',
      maxHeight: ''
    }

    // vu que gap = 1rem, cols - 1 => spaceHeight
    const spaceHeight = cols - 1

    sizes.minHeight = (spaceBetween ? (32 - spaceHeight) / cols : 32) + 'rem'
    if (fullHeight) {
      sizes.maxHeight = (spaceBetween ? (62 - spaceHeight) / cols : 62) + 'rem'
      sizes.height = spaceBetween ? `calc((-${spaceHeight}rem + 100vh)/${cols})` : `calc(100vh / ${cols})`
    } else {
      sizes.maxHeight = (spaceBetween ? (62 - 10 * 2 - spaceHeight) / cols : 62 - 10 * 2) + 'rem'
      sizes.height = spaceBetween ? `calc((-${12 + spaceHeight}rem + 100vh)/${cols})` : `calc((100vh - 12rem)/${cols})`
    }

    return sizes
  }, [cols])
  return (
    <>
      <div
        className={classNames(
          'relative mx-auto space-y-12 px-6 sm:px-8 md:mx-0 md:flex md:h-full md:max-h-[62rem] md:space-y-0',
          alignement,
          { 'md:pr-24': rightPadding },
          { 'md:py-40': !fullHeight && !alignTop }
        )}>
        {(title || description.content.length > 0) && (
          <div className="flex h-full flex-col justify-center md:mr-16">
            <div>
              {title ? <h2 className="font-serif text-5xl font-bold">{title}</h2> : null}{' '}
              {/* <h3 className="font-medium uppercase">{subtitle}</h3> */}
              {/* <div className="mt-4 md:max-w-[18rem]">
                <p className="hyphenate">{description[0]}</p>
                <p className="hyphenate mt-4">{description[1]}</p>
              </div> */}
              <div className="mt-4 flex flex-col space-y-6 md:max-w-[18rem]">
                {description.content.map((thisContent) => (
                  <p key={thisContent} className="hyphenate flex-1 font-light leading-relaxed">
                    {thisContent}
                  </p>
                ))}
              </div>
            </div>
          </div>
        )}
        <div className={classNames('flex flex-col', { 'justify-center': !alignTop, 'justify-start': alignTop })}>
          <div className={classNames('featuredImage-grid grid w-full', { 'gap-4': spaceBetween })}>
            {images.map((thisImage) => (
              <div key={thisImage.url + thisImage.width + thisImage.height} className="singleImage relative">
                <Image src={thisImage.url} layout="fill" className="absolute object-cover" objectFit="cover" />
              </div>
            ))}
          </div>
        </div>
        {variant === 1 && (
          <div
            aria-hidden="true"
            className="absolute left-[-8.5rem] bottom-10 -z-10 hidden h-36 w-72 rounded-t-full bg-orange-200 md:block"
          />
        )}
        {variant === 2 && (
          <div
            aria-hidden="true"
            className="absolute right-10 top-10 -z-10 hidden h-36 w-72 rounded-t-full bg-orange-200 md:block"
          />
        )}
        {variant === 3 && (
          <div
            aria-hidden="true"
            className="absolute left-0 -top-20 -z-10 hidden h-[30rem] w-[15rem] rounded-r-full bg-orange-200 md:block"
          />
        )}
        {/* 
        <div
          className="absolute -z-10 -top-24 right-24 hidden w-48 h-96 bg-transparent border-4 border-blue-200 rounded-l-full md:block"
          aria-hidden="true"
        />
        */}
      </div>
      <style jsx>{`
        .featuredImage-grid {
          grid-template-columns: repeat(${mobileCols}, minmax(0, 1fr));
          grid-template-rows: repeat(${Math.ceil(images.length / mobileCols)}, minmax(0, 1fr));
        }

        .singleImage {
          width: 100%;
          padding-bottom: ${ratio * 100}%;
        }

        @media (min-width: 768px) {
          .featuredImage-grid {
            grid-template-columns: repeat(${Math.ceil(images.length / cols)}, minmax(0, 1fr));
            grid-template-rows: repeat(${cols}, minmax(0, 1fr));
          }

          .singleImage {
            padding-bottom: 0;
            min-height: ${imageSizes.minHeight};
            min-width: calc(${imageSizes.minHeight} / ${ratio});
            height: ${imageSizes.height};
            width: calc(${imageSizes.height} / ${ratio});
            max-height: ${imageSizes.maxHeight};
            max-width: calc(${imageSizes.maxHeight} / ${ratio});
          }
        }
      `}</style>
    </>
  )
}
